import { Rows3, Rows4 } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { IconBtn } from "../../ui";
import type { WorkDensity } from "./density";

/**
 * The switch between the roomy screen and the packed one.
 *
 * It sits in the list pane's head, beside the search box and the filter button,
 * because that is where the operator already looks when they want the list to
 * behave differently - and because the list is where the difference is largest.
 * Both workspace screens put it in the same place, so it is one button in one
 * spot rather than a thing to go looking for per screen.
 *
 * A pressed toggle rather than a menu with two entries: there are exactly two
 * states, the change is visible the instant it happens, and a menu would cost
 * two clicks to say one thing. The accessible name stays the same in both states
 * ("Compact view") and `aria-pressed` carries whether it is on, which is what a
 * screen reader expects from a switch; the icon and the filled pill are the
 * sighted half of the same message.
 *
 * `ns` picks the wording, not the behaviour: the hint under the cursor says what
 * this screen gets out of it ("fit more mail" / "fit more calls"), which is the
 * only part of the button that is not screen-independent.
 */
export function DensityToggle({
  density,
  onToggle,
  ns,
}: {
  density: WorkDensity;
  onToggle: () => void;
  /** The message namespace holding this screen's `density.*` wording. */
  ns: "mail" | "calls";
}) {
  const t = useT();
  const compact = density === "compact";
  const label = t(`${ns}.density.compact`);

  return (
    <IconBtn
      className={cn(compact && "is-on")}
      onClick={onToggle}
      aria-pressed={compact}
      aria-label={label}
      title={compact ? t(`${ns}.density.toCozy`) : t(`${ns}.density.toCompact`)}
    >
      {compact ? <Rows4 className="size-4" /> : <Rows3 className="size-4" />}
    </IconBtn>
  );
}
